import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiLinks } from '@cutting/app.api-links';
import {
  customResponse,
  CuttingClientMaterial,
  ICustomResponse,
  MaterialType,
  StandardCuttingSizes,
} from 'cloud-shared-lib';
import _ from 'lodash';
import { BehaviorSubject, Observable, tap } from 'rxjs';

type MaterialServiceName = 'facade' | 'model' | 'countertop' | 'product' | 'panel';

export type MaterialList = {
  [key in MaterialServiceName]: CuttingClientMaterial[];
};

@Injectable({
  providedIn: 'root',
})
export class ClientMaterialService {
  materialList: MaterialList = {
    facade: [],
    model: [],
    countertop: [],
    product: [],
    panel: [],
  };
  materials$: BehaviorSubject<CuttingClientMaterial[]> = new BehaviorSubject([]);

  constructor(private http: HttpClient) {}

  updateAllMaterials(material: CuttingClientMaterial): void {
    const currentMaterials = this.materials$.value;
    const materialIndex = currentMaterials.findIndex(this.comparer(material.name, material.article));

    if (materialIndex === -1) {
      this.materials$.next([...currentMaterials, material]);
    } else {
      const updatedMaterials = [...currentMaterials];
      updatedMaterials[materialIndex] = material;
      this.materials$.next(updatedMaterials);
    }
  }

  replaceAllMaterials(oldMaterial: CuttingClientMaterial, newMaterial: CuttingClientMaterial): void {
    const currentMaterials = this.materials$.value;
    const materialIndex = currentMaterials.findIndex(this.comparer(oldMaterial.name, oldMaterial.article));

    if (materialIndex !== -1) {
      const updatedMaterials = currentMaterials.map((m, index) => (index === materialIndex ? newMaterial : m));
      this.materials$.next(updatedMaterials);
    }
  }

  updateMaterials(type: MaterialServiceName, material: CuttingClientMaterial): void {
    const materialsType = this.materialList[type];
    if (materialsType) {
      const hasIndex = materialsType.findIndex(this.comparer(material.name, material.article));
      this.updateAllMaterials(material);
      if (hasIndex === -1) {
        this.materialList[type] = [...materialsType, material];
      } else {
        const updatedMaterials = [...materialsType];
        updatedMaterials[hasIndex] = material;
        this.materialList[type] = updatedMaterials;
      }
    }
  }

  replaceMaterial(
    type: MaterialServiceName,
    newMaterial: CuttingClientMaterial,
    oldMaterialName: string,
    oldMaterialArticle: string
  ): void {
    const materials = this.materialList[type];

    if (materials) {
      const hasIndex = materials.findIndex(this.comparer(oldMaterialName, oldMaterialArticle));

      if (hasIndex !== -1) {
        const oldMaterial = materials[hasIndex];
        materials[hasIndex] = newMaterial;
        this.replaceAllMaterials(oldMaterial, newMaterial);
      }
    }
  }

  // Подготавливает материал к удалению из списка по типу
  prepareForDeletion(type: MaterialServiceName, materialName: string, materialArticle: string): void {
    const materials = this.materialList[type];
    const materialIndex = materials.findIndex(this.comparer(materialName, materialArticle));

    if (materialIndex === -1) return;

    const existsInOtherTypes = this.checkExistenceInOtherTypes(type, materialName, materialArticle);
    materials.splice(materialIndex, 1);

    if (!existsInOtherTypes) {
      this.removeMaterialFromAllMaterials(materialName, materialArticle);
    }
  }

  loadClientMaterials(orderId: number): void {
    this.http
      .get(ApiLinks.clientMaterials + `/order/${orderId}`)
      .pipe(customResponse<CuttingClientMaterial[]>())
      .subscribe(res => {
        if (res.result) {
          this.loadMaterials(res.result);
        }
      });
  }

  editClientMaterialCuttings(item: CuttingClientMaterial): void {
    const materials = this.materials$.value;

    const updatedMaterials = materials.map(material =>
      this.comparer(item.name, item.article)(material) ? { ...material, cuttings: [...item.cuttings] } : material
    );
    this.materials$.next(updatedMaterials);

    Object.keys(this.materialList).forEach(key => {
      const type = key as MaterialServiceName;
      this.materialList[type] = this.materialList[type].map(material =>
        this.comparer(item.name, item.article)(material) ? { ...material, cuttings: [...item.cuttings] } : material
      );
    });
  }

  saveClientMaterials(orderId: number): Observable<ICustomResponse<CuttingClientMaterial[]>> {
    return this.http.put(ApiLinks.clientMaterials + `/order/${orderId}`, this.materials$.value).pipe(
      customResponse<CuttingClientMaterial[]>(),
      tap(res => {
        if (res.result) {
          this.loadMaterials(res.result);
        }
      })
    );
  }

  getStandardCuttingsSizes(
    materialName: string,
    materialArticle: string,
    materialType: MaterialType,
    orderId?: number
  ): Observable<StandardCuttingSizes[]> {
    let params = new HttpParams({
      fromObject: {
        materialName: materialName,
        materialCode: materialArticle,
        isSheetMaterial: materialType === MaterialType.Plate,
      },
    });

    if (orderId) {
      params = params.append('orderId', orderId);
    }

    return this.http.get<StandardCuttingSizes[]>(ApiLinks.clientMaterials + `/material-type-sizes`, {
      params,
    });
  }

  getMaterials(type: MaterialServiceName): CuttingClientMaterial[] {
    return this.materialList[type] ?? [];
  }

  resetMaterials(): void {
    this.materialList = {
      facade: [],
      model: [],
      countertop: [],
      product: [],
      panel: [],
    };
    this.materials$.next([]);
  }

  private checkExistenceInOtherTypes(
    type: MaterialServiceName,
    materialName: string,
    materialArticle: string
  ): boolean {
    return _.some(
      this.materialList,
      (typeList, key) => key !== type && _.some(typeList, this.comparer(materialName, materialArticle))
    );
  }

  private loadMaterials(newMaterials: CuttingClientMaterial[]): void {
    const materials = this.materials$.value.filter(m => !m.preparedForDeletion);

    newMaterials.forEach(newMaterial => {
      const existingIndex = materials.findIndex(this.comparer(newMaterial.name, newMaterial.article));

      if (existingIndex === -1) {
        materials.push(newMaterial);
      } else {
        materials[existingIndex] = newMaterial;
      }
    });

    this.materials$.next(materials);
  }

  private comparer(materialName: string, materialArticle: string): (m: CuttingClientMaterial) => boolean {
    return (m): boolean => m.name + m.article === materialName + materialArticle;
  }

  private removeMaterialFromAllMaterials(materialName: string, materialArticle: string): void {
    const updatedMaterials = this.materials$.value.filter(
      material => !this.comparer(materialName, materialArticle)(material)
    );
    this.materials$.next(updatedMaterials);
  }
}
